// Type x Variable generation system (Phase 2)

export interface TweetTemplate {
  type: string;
  description: string;
  pattern: string;
  slots: string[];  // Variables to fill in
  example: string;
}

// Tweet template collection
export const TWEET_TEMPLATES: TweetTemplate[] = [
  {
    type: 'Metaphor',
    description: 'Compare A to B',
    pattern: '{A} is basically {B}',
    slots: ['A', 'B'],
    example: 'Programming is basically cooking with worse instructions',
  },
  {
    type: 'Self-deprecation > Punchline',
    description: 'Complaint > Positive spin',
    pattern: '{complaint}\n\n{positive spin}',
    slots: ['complaint', 'positive spin'],
    example: "Spent 4 hours on a bug and wanted to cry\n\nDelegated it to tomorrow-me, he's got this",
  },
  {
    type: 'Observation',
    description: 'Observation > One-liner',
    pattern: '{observation}. {one-liner}',
    slots: ['observation', 'one-liner'],
    example: 'My coffee consumption is out of control. The correlation with productivity is terrifying',
  },
  {
    type: 'Question',
    description: 'Context > Question',
    pattern: '{context}\n\n{question}',
    slots: ['context', 'question'],
    example: "Restarted VSCode 3 times today\n\nIs it just me?",
  },
  {
    type: 'Relatable List',
    description: 'List 3 things > Wrap-up',
    pattern: '{item1}\n{item2}\n{item3}\n\n{wrap-up}',
    slots: ['item1', 'item2', 'item3', 'wrap-up'],
    example: "Morning: write code\nAfternoon: fix errors\nNight: write more code\n\nEndless loop",
  },
  {
    type: 'Timeline',
    description: 'Past > Present > Conclusion',
    pattern: '{past} > {present} > {conclusion}',
    slots: ['past', 'present', 'conclusion'],
    example: "Yesterday: broken. This morning: fixed it. Turns out: it was a typo",
  },
  {
    type: 'Contrast',
    description: 'Expectation vs Reality',
    pattern: 'Expectation: {expectation}\nReality: {reality}',
    slots: ['expectation', 'reality'],
    example: "Expectation: early to bed, early to rise\nReality: 3am LeetCode session",
  },
  {
    type: 'Discovery',
    description: 'Experience > Discovery > Takeaway',
    pattern: '{experience} and {discovery}. {takeaway}',
    slots: ['experience', 'discovery', 'takeaway'],
    example: "Worked from a coffee shop and actually focused. Environment matters",
  },
  {
    type: 'Number Impact',
    description: 'Impact through numbers',
    pattern: '{number} {unit} to {result}',
    slots: ['number', 'unit', 'result'],
    example: "3 hours to squash 100 bugs",
  },
  {
    type: 'Resolution',
    description: 'Current state > Resolution',
    pattern: '{current state}. {resolution}',
    slots: ['current state', 'resolution'],
    example: "Running on empty. Going all in starting tomorrow",
  },
  {
    type: 'Empathy Hook',
    description: 'Relatable moment > Question',
    pattern: '{relatable moment}. {question}?',
    slots: ['relatable moment', 'question'],
    example: "Can't stop thinking about code in bed. Anyone else?",
  },
  {
    type: 'Conclusion First',
    description: 'Conclusion > Reason',
    pattern: 'Conclusion: {conclusion}\nReason: {reason}',
    slots: ['conclusion', 'reason'],
    example: "Conclusion: sleep is the ultimate hack\nReason: today's bug fixed itself after a nap",
  },
  {
    type: 'Prediction',
    description: 'Prediction > Evidence',
    pattern: '{prediction}. {evidence}',
    slots: ['prediction', 'evidence'],
    example: "AI will replace Stack Overflow. I haven't opened it in weeks",
  },
  {
    type: 'Regret',
    description: 'Regret > Lesson',
    pattern: '{regret}. {lesson}',
    slots: ['regret', 'lesson'],
    example: "Didn't make a backup. Never again",
  },
  {
    type: 'Surprise',
    description: 'Shocking fact > Reaction',
    pattern: '{fact}. {reaction}',
    slots: ['fact', 'reaction'],
    example: "It was my birthday today. Completely forgot",
  },
  {
    type: 'Before/After',
    description: 'Record of change',
    pattern: 'Before: {before}\nAfter: {after}',
    slots: ['before', 'after'],
    example: "Before: 100 bugs\nAfter: 1 bug (it multiplied)",
  },
  {
    type: 'Ranking',
    description: 'Top 3 format',
    pattern: 'My personal {topic} Top 3\n1. {first}\n2. {second}\n3. {third}',
    slots: ['topic', 'first', 'second', 'third'],
    example: "My personal editor Top 3\n1. VSCode\n2. Vim\n3. Notepad",
  },
  {
    type: 'Hypothetical',
    description: 'If X then Y',
    pattern: 'If {hypothetical}, {result}',
    slots: ['hypothetical', 'result'],
    example: "If I had a time machine, I'd go punch yesterday-me",
  },
  {
    type: 'Myth Busting',
    description: 'Myth > Truth',
    pattern: 'Myth: {myth}\nTruth: {truth}',
    slots: ['myth', 'truth'],
    example: "Myth: programmers are math geniuses\nTruth: we're Google geniuses",
  },
  {
    type: 'Fake Wisdom',
    description: 'Pseudo-profound quote',
    pattern: '{quote}. {qualifier}',
    slots: ['quote', 'qualifier'],
    example: "Consistency is key. I last 3 days max though",
  },
];

export const templateService = {
  /**
   * Build a generation prompt using templates
   */
  buildTemplatePrompt(
    inputText: string,
    stylePrompt: string,
    fewShotExamples: string[],
    buzzInsights: string[],
    totalCount: number = 20
  ): string {

    // Select optimal types based on buzzInsights (while maintaining diversity)
    const templateCount = Math.min(4, Math.floor(totalCount / 5));
    const perTemplateCount = Math.floor(totalCount / templateCount);

    const selectedTemplates = buzzInsights.length > 0
      ? this.selectOptimalTemplates(buzzInsights, templateCount)
      : this.selectRandomTemplates(templateCount);

    const templateInstructions = selectedTemplates.map((template, idx) => {
      return `
**Template ${idx + 1}: ${template.type}**
Pattern: ${template.pattern}
Example: ${template.example}
-> Generate ${perTemplateCount} drafts using this template`;
    }).join('\n');

    return `Based on the following content, generate a total of 20 tweet drafts using the 4 specified templates (5 drafts per template).

**Input Content:**
${inputText}

**Style Instructions (must follow strictly):**
${stylePrompt}

**Reference Examples (this user's actual tweets):**
${fewShotExamples.length > 0 ? fewShotExamples.map((t, i) => `${i + 1}. ${t}`).join('\n') : '(no examples)'}

**Viral Patterns:**
${buzzInsights.length > 0 ? buzzInsights.map((insight, i) => `${i + 1}. ${insight}`).join('\n') : '(no analysis data)'}

${templateInstructions}

**Generation Rules (required):**
1. **280 characters max** - Even 1 character over is a fail
2. **Perfectly match the style** - Catchphrases, sentence endings, emoji usage, punctuation must all match
3. **Be specific** - Include at least one proper noun, number, or concrete moment
4. **Follow the template** - Stick to each template's pattern (no freeform)
5. **Variety** - Use different angles even within the same template
6. **No repetition** - No cliched phrases or repeated hooks

**Output Format (JSON):**
{
  "tweets": [
    {"text": "Tweet 1", "type": "Metaphor"},
    {"text": "Tweet 2", "type": "Metaphor"},
    ...(20 total, with type labels)
  ]
}`;
  },

  /**
   * Randomly select templates
   */
  selectRandomTemplates(count: number): TweetTemplate[] {
    const shuffled = [...TWEET_TEMPLATES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  },

  /**
   * Select optimal templates based on buzzInsights
   */
  selectOptimalTemplates(buzzInsights: string[], count: number = 4): TweetTemplate[] {
    const scores = new Map<string, number>();

    // Score each template type
    for (const template of TWEET_TEMPLATES) {
      let score = 0;

      for (const insight of buzzInsights) {
        const lower = insight.toLowerCase();

        // Question-related -> Question, Empathy Hook
        if ((lower.includes('question') || lower.includes('ask')) &&
            (template.type === 'Question' || template.type === 'Empathy Hook')) {
          score += 2;
        }

        // Numbers -> Number Impact, Ranking
        if (lower.includes('number') &&
            (template.type === 'Number Impact' || template.type === 'Ranking')) {
          score += 2;
        }

        // Experience/story -> Discovery, Timeline, Regret
        if ((lower.includes('experience') || lower.includes('example') || lower.includes('story')) &&
            (template.type === 'Discovery' || template.type === 'Timeline' || template.type === 'Regret')) {
          score += 2;
        }

        // Empathy -> Empathy Hook, Relatable List
        if (lower.includes('empathy') &&
            (template.type === 'Empathy Hook' || template.type === 'Relatable List')) {
          score += 2;
        }

        // Line breaks -> Relatable List, Contrast, Ranking
        if (lower.includes('line break') &&
            (template.type === 'Relatable List' || template.type === 'Contrast' || template.type === 'Ranking')) {
          score += 1;
        }

        // Conclusion -> Conclusion First, Resolution
        if (lower.includes('conclusion') &&
            (template.type === 'Conclusion First' || template.type === 'Resolution')) {
          score += 2;
        }
      }

      // Keep some base randomness
      score += Math.random() * 0.5;

      scores.set(template.type, score);
    }

    // Sort by score
    const sorted = TWEET_TEMPLATES
      .slice()
      .sort((a, b) => (scores.get(b.type) || 0) - (scores.get(a.type) || 0));

    return sorted.slice(0, count);
  },

  /**
   * Get all templates
   */
  getAllTemplates(): TweetTemplate[] {
    return TWEET_TEMPLATES;
  },

  /**
   * Build a generation prompt with specific template types
   */
  buildCustomTemplatePrompt(
    inputText: string,
    stylePrompt: string,
    templateTypes: string[],
    perTemplateCount: number = 5
  ): string {
    const templates = TWEET_TEMPLATES.filter(t => templateTypes.includes(t.type));

    if (templates.length === 0) {
      throw new Error('No matching template types found');
    }

    const templateInstructions = templates.map((template, idx) => {
      return `
**Template ${idx + 1}: ${template.type}**
Pattern: ${template.pattern}
-> Generate ${perTemplateCount} drafts using this template`;
    }).join('\n');

    return `Based on the following content, generate tweet drafts using the specified templates.

**Input Content:**
${inputText}

**Style Instructions:**
${stylePrompt}

${templateInstructions}

**Output Format (JSON):**
{
  "tweets": [
    {"text": "Tweet 1", "type": "${templates[0].type}"},
    ...
  ]
}`;
  },
};
